import { splitDatesIntoIntervals } from "@cmp-src/data-display/date-intervals/splitDatesIntoIntervals";
import { cn } from "@gettactic/helpers";
import { useAuthenticatedOptional } from "@gettactic/hooks";
import type { DateRange, DateValue } from "@cmp-src/calendar/types";
import type { IconProps } from "@cmp-src/data-display/Icon/Icon";
import { Button, type ButtonProps } from "@cmp-src/primitives/button";
import {
    ArrowDownIcon,
    Calendar1Icon,
    Calendar2Icon
} from "@cmp-src/icons/icons";
import { differenceInDays } from "date-fns";
import React from "react";

/**
 * Extra button attributes are forwarded to the button, so a popover can use the trigger
 * itself (`asChild`) instead of wrapping it in a second button.
 */
export interface CalendarTriggerProps
    extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
    dates: DateValue | undefined;
    customText?: string;
    buttonProps?: ButtonProps;
    icon?: React.FC<IconProps>;
    className?: string;
    separator?: React.ReactNode;
    /** When true, shows "X day(s)" count for multiple date selections */
    showDaysCount?: boolean;
    /** When false, hides day name (e.g., "Mon") from date display */
    showDayName?: boolean;
    /** When true, hides the "Multiple" text from the trigger */
    hideMultipleText?: boolean;
    /** When true, shows border and no background (for Dashboard). Default: false (shows background, no border for Reserve) */
    variant?: "default" | "outlined";
    /** Name of the field, announced before the dates by screen readers. */
    label?: string;
}

const classes = {
    Button: "w-[272px] h-[42px]",
    Label:
        "flex flex-1 flex-row justify-center items-center font-inter font-normal",
    Icon: "size-4 text-neutral-600 hidden md:block",
    IntervalItem:
        "flex flex-row items-center justify-center gap-0 font-inter text-neutral-1000",
    IntervalSeparator: "mr-1 font-inter",
    DropdownArrow: "size-4 ml-[5px] hidden md:block"
};

export const CalendarTrigger = React.forwardRef<
    React.ComponentRef<typeof Button>,
    CalendarTriggerProps
>(
    (
        {
            dates,
            buttonProps,
            icon,
            className,
            customText,
            separator = ",",
            showDaysCount,
            showDayName = true,
            hideMultipleText = false,
            variant = "default",
            label,
            ...triggerProps
        },
        ref
    ) => {
        // Optional: this trigger also renders on guest/public pages (e.g. a
        // shared request's date field), which have no AppAuthenticatedContext
        // — falls back to splitDatesIntoIntervals' own default format there.
        const authenticated = useAuthenticatedOptional();
        const intervals = splitDatesIntoIntervals(
            dates,
            authenticated?.userContext.authenticatedUser?.user?.date_format,
            showDayName
        );

        // Use separator as arrow for range display, or default to arrow symbol
        const arrow =
            separator !== "," ? (
                separator
            ) : (
                <span className="px-1 text-neutral-500 font-inter"> ➝ </span>
            );

        const renderDaysCountBadge = (count: number) => {
            return (
                <span className="ml-2 rounded-full bg-neutral-100 px-2 py-0.5 text-neutral-700 font-inter">
          {count === 1 ? "1 day" : `${count} days`}
        </span>
            );
        };

        const multipleWord = hideMultipleText ? "" : "Multiple";

        const renderMultipleDatesLabel = () => {
            if (!Array.isArray(dates)) return null;
            const count = dates.length;
            if (showDaysCount) {
                return (
                    <>
                        {multipleWord}
                        {renderDaysCountBadge(count)}
                    </>
                );
            }
            return (
                <>
                    {multipleWord} ({count})
                </>
            );
        };

        const isDateRange = (value: DateValue | undefined): value is DateRange => {
            return !!value && !Array.isArray(value) && !(value instanceof Date);
        };

        const getRangeDaysCount = (): number | null => {
            if (!isDateRange(dates) || !dates.from) return null;
            const from = dates.from;
            const to = dates.to ?? dates.from;
            return differenceInDays(to, from) + 1;
        };

        const renderIntervalsWithCount = (daysCount: number | null) => {
            // For multiple intervals, use comma as separator
            const intervalSeparator = ",";

            return (
                <>
                    {intervals.map((interval, index) =>
                            interval.length < 3 ? (
                                <span
                                    className={classes.IntervalItem}
                                    key={`date-interval-${interval[0]}`}
                                >
                <span className={"font-inter"}>{interval[0]}</span>
                                    {interval.length === 2 && <span>{arrow}</span>}
                                    <span className={"font-inter"}>{interval[1]}</span>
                                    {index < intervals.length - 1 && (
                                        <span className={classes.IntervalSeparator}>
                    {intervalSeparator}
                  </span>
                                    )}
              </span>
                            ) : (
                                <span
                                    className={"font-inter"}
                                    key={`date-interval-${interval[0]}`}
                                >
                {interval[0]}
                                    {arrow}
                                    {interval[1]}
              </span>
                            )
                    )}
                    {showDaysCount &&
                        daysCount !== null &&
                        renderDaysCountBadge(daysCount)}
                </>
            );
        };

        const variantStyles =
            variant === "outlined"
                ? "border border-input bg-background"
                : "border-0 bg-neutral-50";

        return (
            <Button
                type="button"
                variant={variant === "outlined" ? "outline" : "ghost"}
                {...triggerProps}
                className={cn(classes.Button, variantStyles, className)}
                ref={ref}
                {...buttonProps}
            >
                {label && <span className="sr-only">{label}: </span>}
                {React.createElement(icon || Calendar2Icon, {
                    className: classes.Icon
                })}
                <span className={classes.Label}>
          {(() => {
              if (customText) {
                  return customText;
              }
              if (!intervals || !intervals.length) {
                  return <>Select a date</>;
              }
              // Multiple dates (array)
              if (
                  Array.isArray(dates) &&
                  (dates.length > 1 || (showDaysCount && dates.length >= 1))
              ) {
                  return renderMultipleDatesLabel();
              }
              // Default: show intervals (with optional day count for ranges)
              const rangeDaysCount = getRangeDaysCount();
              return renderIntervalsWithCount(rangeDaysCount);
          })()}
                    <ArrowDownIcon className={classes.DropdownArrow} />
        </span>
            </Button>
        );
    }
);
